import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Button, CssBaseline, IconButton, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useAppDispatch } from "../../Redux/hooks";
import AppTheme from "../../theme/AppTheme";
import PaginationButtons from "../../components/Pagination/PaginationButtons";
import { dataGridCustomizations } from "../../theme/customizations";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getOrderDetails,
  viewOrderDetails,
} from "../../service/order/orderService";
import { setOrderDetails } from "../../Redux/Slices/orderSlice";
import InvoiceOptionsDialog from "./Fragments/InvoiceOptionsDialog/InvoiceOptionsDialog";
import HideSourceIcon from "@mui/icons-material/HideSource";
import { styled } from "@mui/material/styles";
import { notifyError } from "../../components/ToastContainer/Toast/Toast";
import GenericFilter from "../../components/GenericFilter/GenericFilter";
import { storeActiveStoreId } from "../../utils/indexedDBUtils";
import { getRemainingQty } from "./Utils/orderItemQuantity";
import OrderTableSkeleton from "./Fragments/OrderTableSkeleton";
import { dispatchResetLoader } from "../../Redux/Utils/loaderDispatcher";

const xThemeComponents = {
  ...dataGridCustomizations,
};

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 12,
  },
}));

export default function Orders(props: { disableCustomTheme?: boolean }) {
  const dispatch = useAppDispatch();
  const { store_id } = useParams();
  const [filteredOrders, setFilteredOrders] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(0);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filters, setFilters] = useState<any>(null);
  const [flag, setFlag] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [openDialog, setOpenDialog] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { triggerId } = (location.state || {}) as {
    triggerId?: number;
    triggerType?: string;
    fromNotification?: boolean;
  };

  const applyOrdersResponse = useCallback(
    (response: any) => {
      const data = response.data.data.orders || [];
      const resData = response.data.data;

      dispatch(setOrderDetails(resData.orders));
      setFilteredOrders(data);
      setTotal(resData.total);
      setLimit(resData.limit);
      setTotalPages(resData.totalPages);
    },
    [dispatch]
  );

  const reloadOrdersList = useCallback(async () => {
    if (!store_id) {
      return;
    }

    setFilteredOrders([]);
    setListLoading(true);
    setListError(null);
    dispatchResetLoader();

    try {
      const response = await getOrderDetails(currentPage, store_id);
      applyOrdersResponse(response);
    } catch {
      const message = "Failed to load orders. Please try again.";
      setListError(message);
      notifyError("Something went wrong!");
      setFilteredOrders([]);
      setTotal(0);
      setLimit(10);
      setTotalPages(0);
    } finally {
      setListLoading(false);
    }
  }, [applyOrdersResponse, currentPage, store_id]);

  const fetchData = async (filtersData?: any, isFromFilter?: boolean) => {
    if (isFromFilter === false) {
      setFlag(false);
      setFilters(null);
      await reloadOrdersList();
      return;
    }

    if (
      filtersData !== undefined &&
      filtersData !== null &&
      Object.keys(filtersData).length > 0
    ) {
      setFilters(filtersData || {});
      setFlag(!!isFromFilter);
      setFilteredOrders(filtersData?.data || []);
      setListLoading(false);
      setListError(null);
      if (typeof filtersData?.total === "number") setTotal(filtersData.total);
      if (typeof filtersData?.totalPages === "number") {
        setTotalPages(filtersData.totalPages);
      }
      return;
    }

    setFlag(false);
    await reloadOrdersList();
  };

  // ✅ CRITICAL: Set ACTIVE_STORE_ID when Orders page loads with store_id from URL
  // This ensures notifications work when user navigates directly to orders page
  useEffect(() => {
    if (store_id && Number(store_id) > 0) {
      const storeIdStr = `${store_id}`;
      const currentActiveStoreId = localStorage.getItem('ACTIVE_STORE_ID');
      if (currentActiveStoreId !== storeIdStr) {
        localStorage.setItem('ACTIVE_STORE_ID', storeIdStr);
        localStorage.setItem('CURRENT_STORE_ID', storeIdStr);
        // Sync to IndexedDB for service worker access
        storeActiveStoreId(storeIdStr).catch(err => {
          console.error('[Orders] Failed to sync ACTIVE_STORE_ID to IndexedDB:', err);
        });
        console.log(`[Orders] ✅ Orders page loaded: Set ACTIVE_STORE_ID to ${store_id} (notifications will trigger for this store)`);
      }
    }
  }, [store_id]);

  useEffect(() => {
    if (flag || !store_id) {
      return;
    }

    let cancelled = false;

    const loadOrders = async () => {
      setFilteredOrders([]);
      setListLoading(true);
      setListError(null);
      dispatchResetLoader();

      try {
        const response = await getOrderDetails(currentPage, store_id);
        if (cancelled) {
          return;
        }
        applyOrdersResponse(response);
      } catch {
        if (cancelled) {
          return;
        }
        const message = "Failed to load orders. Please try again.";
        setListError(message);
        notifyError("Something went wrong!");
        setFilteredOrders([]);
        setTotal(0);
        setLimit(10);
        setTotalPages(0);
      } finally {
        if (!cancelled) {
          setListLoading(false);
        }
      }
    };

    loadOrders();

    return () => {
      cancelled = true;
    };
  }, [applyOrdersResponse, currentPage, flag, store_id]);

  const convertToLocalDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const rows = filteredOrders?.map((order: any) => ({
    order_id: order.id,
    order_date: convertToLocalDate(order.order_date),
    method: order.payment_method,
    amount: order.total_amount,
    status: order.status,
    order_details:
      order.order_details?.map((orderData: any) => ({
        product_id: orderData?.product.id,
        product_name: orderData?.product_name,
        quantity: orderData?.quantity || 0,
      })) || [],
    customer_name: order.customer.name,
    customer_email: order.customer.email,
    customer_phone: order.customer.phone,
  }));

  const columns: GridColDef[] = useMemo(
    () => [
      { field: "order_date", headerName: "Date", minWidth: 160, flex: 1 },
      { field: "order_id", headerName: "Order No", minWidth: 130, flex: 1 },
      {
        field: "customer_name",
        headerName: "Customer Name",
        minWidth: 222,
        flex: 1,
      },
      { field: "method", headerName: "Payment Type", minWidth: 150, flex: 1 },
      { field: "amount", headerName: "Amount (₹)", minWidth: 155, flex: 1 },
      { field: "status", headerName: "Status", minWidth: 175, flex: 1 },
      {
        field: "action",
        headerName: "Action",
        minWidth: 120,
        flex: 1,
        renderCell: (params) => {
          const currentStatus = params.row.status;
          const isFinalStatus = [
            "billed",
            "shipped",
            "cancelled",
            "completed",
          ].includes(currentStatus);

          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const handleActionClick = async () => {
            try {
              const res = await viewOrderDetails(params.row.order_id);
              if (res.status === 200) {
                const orderItems = res.data.data.order_items || [];
                dispatch(setOrderDetails(res.data.data));

                const allZeroRemaining =
                  orderItems.length > 0 &&
                  orderItems.every((item) => getRemainingQty(item) === 0);

                if (allZeroRemaining) {
                  setOpenDialog(false);
                  localStorage.setItem("modalState", "closed");
                  notifyError("All order items are fully processed");
                  return;
                }
                localStorage.removeItem("modalState");
                setOpenDialog(true);
              }
            } catch {
              notifyError("Something went wrong!");
              setOpenDialog(false);
            }
          };
          return (
            <Box display="flex" alignItems="center" gap={1} mt={0.65}>
              <LightTooltip title="Disabled" placement="right">
                {isFinalStatus ? (
                  <LightTooltip title="Disabled" placement="right">
                    <IconButton disabled>
                      <HideSourceIcon fontSize="small" />
                    </IconButton>
                  </LightTooltip>
                ) : triggerId ? (
                  <InvoiceOptionsDialog params={params} />
                ) : (
                  <InvoiceOptionsDialog params={params} />
                )}
              </LightTooltip>
            </Box>
          );
        },
      },
      {
        field: "view",
        headerName: "View",
        minWidth: 100,
        flex: 1,
        renderCell: (params) => {
          const orderId = params.row.order_id;
          const orderStatus = params.row.status?.toLowerCase();
          const isPending = orderStatus === "pending";
          
          return (
            <LightTooltip title={isPending ? "Disabled" : "View"} placement="right">
              <span>
                <IconButton
                  color="secondary"
                  disabled={isPending}
                  onClick={() => {
                    if (!isPending) {
                      navigate(`/stores/${store_id}/orders-view/${orderId}`);
                    }
                  }}
                >
                  <VisibilityIcon />
                </IconButton>
              </span>
            </LightTooltip>
          );
        },
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ],
    []
  );

  const handlePageChange = (page: number) => {
    if (page === currentPage) {
      return;
    }

    if (!flag) {
      setFilteredOrders([]);
      setListLoading(true);
      dispatchResetLoader();
    }

    setCurrentPage(page);
  };

  const handleRetryOrders = () => {
    reloadOrdersList();
  };

  const showOrderSkeleton = listLoading && !flag;
  const showOrderError =
    Boolean(listError) && filteredOrders.length === 0 && !flag && !listLoading;

  const resetCallback = () => {
    setFilters(null);
    setFlag(false);
    setFilteredOrders([]);
  };

  const CustomPagination = () => (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        py: 1,
        px: 1,
      }}
    >
      <PaginationButtons
        page={currentPage}
        count={totalPages}
        totalItems={total}
        itemsPerPage={limit}
        onChange={handlePageChange}
      />
    </Box>
  );

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <GenericFilter 
          entity="order" 
          handleGetFetchData={fetchData} 
          store_id={store_id} 
          page={currentPage} 
          limit={limit}
          resetCallBack={resetCallback}
        />
      </Box>
      <AppTheme {...props} themeComponents={xThemeComponents}>
        <CssBaseline enableColorScheme />
        <Box
          sx={{
            width: "100%",
            height: 520,
            "&::-webkit-scrollbar": { display: "none" },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          {showOrderSkeleton ? (
            <OrderTableSkeleton />
          ) : showOrderError ? (
            <Box
              sx={{
                py: 6,
                px: 2,
                textAlign: "center",
                bgcolor: "#fff",
                borderRadius: 1,
                border: "1px solid #f0f0f0",
              }}
            >
              <Typography color="error" variant="body1" sx={{ mb: 2 }}>
                {listError}
              </Typography>
              <Button variant="outlined" onClick={handleRetryOrders}>
                Retry
              </Button>
            </Box>
          ) : (
          <DataGrid
            rows={rows}
            columns={columns}
            autosizeOnMount
            paginationMode="server"
            getRowId={(row) => row.order_id}
            slots={{ pagination: CustomPagination }}
            loading={false}
            disableColumnMenu
          />
          )}
        </Box>
      </AppTheme>
    </>
  );
}
